// components/InvitationPage.js - Fixed invitation flow for existing users
import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import { supabase } from '../lib/supabase'
import { Building2, Timer, CheckCircle, AlertCircle, Mail, Lock, User, LogIn } from 'lucide-react'

const InvitationPage = ({ invitationToken }) => {
  const { user, signIn, signUp } = useAuth()
  const { switchTenant, refetchTenants } = useTenant()
  const [invitationData, setInvitationData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isNewUser, setIsNewUser] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false) // MOVED TO TOP
  
  // Form state for authentication
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')

  useEffect(() => {
    if (invitationToken) {
      validateInvitation()
    }
  }, [invitationToken])

  useEffect(() => {
    // If user logs in while on invitation page, auto-accept
    if (user && invitationData && !success) {
      acceptInvitation()
    }
  }, [user, invitationData])

  const validateInvitation = async () => {
    try {
      setLoading(true)
      setError('')

      const { data, error } = await supabase.rpc('accept_invitation', {
        p_invitation_token: invitationToken,
        p_user_id: null // Just validate, don't accept yet
      })

      if (error) throw error

      if (!data.success) {
        throw new Error(data.error)
      }

      setInvitationData(data)
      setEmail(data.invited_email)

      // Check if this email already has a user account in the auth system
      // We'll try to detect this by checking if they can sign in
      // This is a more reliable way than checking user_profiles
      setIsNewUser(true) // Default to new user, will be corrected if they sign in successfully

    } catch (err) {
      console.error('Invitation validation error:', err)
      setError(err.message || 'Invalid or expired invitation')
    } finally {
      setLoading(false)
    }
  }

  const acceptInvitation = async () => {
    if (!user || !invitationData) return

    try {
      setAcceptLoading(true)

      const { data, error } = await supabase.rpc('accept_invitation', {
        p_invitation_token: invitationToken,
        p_user_id: user.id
      })

      if (error) throw error

      if (!data.success) {
        throw new Error(data.error)
      }

      // Refresh tenants and switch to new tenant
      await refetchTenants()
      
      setSuccess(true)
      
      // Auto-switch to the new tenant after a short delay
      setTimeout(() => {
        window.location.href = '/app' // Redirect to main app
      }, 2000)

    } catch (err) {
      console.error('Error accepting invitation:', err)
      setError(err.message || 'Failed to accept invitation')
    } finally {
      setAcceptLoading(false)
    }
  }

  const handleSignIn = () => {
    setShowPasswordForm(true)
    setIsNewUser(false) // They're trying to sign in, so they're existing user
  }

  const handleCreateAccount = () => {
    setShowPasswordForm(true)
    setIsNewUser(true) // They're creating account, so they're new user
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setAcceptLoading(true)
      setError('')

      if (isNewUser) {
        // Create new account
        const { error } = await signUp(email, password, fullName, null) // No tenant creation for invites
        if (error) throw new Error(error)
        
        // Show email confirmation screen instead of error message
        setShowEmailConfirmation(true)
        return // Important: return here to stop execution
      } else {
        // Try to sign in existing user
        const { error } = await signIn(email, password)
        if (error) {
          // If sign in fails, they might be a new user after all
          if (error.includes('Invalid login credentials') || error.includes('Email not confirmed')) {
            setError('Unable to sign in. This might be your first time using Logworthy. Try creating an account instead.')
            // Don't auto-switch to signup, let them decide
          } else {
            throw new Error(error)
          }
        }
        // If successful, acceptInvitation will be called automatically via useEffect
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setAcceptLoading(false)
    }
  }

  // Email confirmation screen component
  const InvitationEmailConfirmationScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Timer className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Check Your Email!</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            We've sent a confirmation link to <strong>{email}</strong>. 
            Click the link in your email to activate your account and join <strong>{invitationData?.tenant_name}</strong>.
          </p>
        </div>

        {/* What's Next Steps - Modified for invitation flow */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200 mb-8">
          <h2 className="text-2xl font-semibold text-slate-800 mb-6">What happens next?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">1. Check Your Email</h3>
              <p className="text-sm text-slate-600">
                Look for an email from Logworthy with the subject "Confirm your account"
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">2. Confirm Account</h3>
              <p className="text-sm text-slate-600">
                Click the confirmation link to activate your account
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">3. Join Team</h3>
              <p className="text-sm text-slate-600">
                Return to this page to automatically join {invitationData?.tenant_name}
              </p>
            </div>
          </div>
        </div>

        {/* Invitation-specific features */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200 mb-8">
          <h2 className="text-2xl font-semibold text-slate-800 mb-6">
            You're joining {invitationData?.tenant_name}
          </h2>
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Organization: {invitationData?.tenant_name}</h3>
                <p className="text-sm text-slate-600">Role: {invitationData?.role || 'Team Member'}</p>
                <p className="text-sm text-slate-600">
                  Invited by: {invitationData?.invited_by_name || 'Team Administrator'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-yellow-800 mb-2">Didn't receive the email?</h3>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• Check your spam or junk folder</li>
            <li>• Make sure you entered the correct email address: {email}</li>
            <li>• Wait a few minutes - emails can sometimes be delayed</li>
            <li>• The invitation link will remain valid until you confirm your account</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="text-center space-y-4">
          <button
            onClick={() => {
              // When they come back after confirming, they should be able to accept the invitation
              window.location.reload()
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-all mr-4"
          >
            Already confirmed? Join Team
          </button>
          
          <button
            onClick={() => setShowEmailConfirmation(false)}
            className="text-slate-600 hover:text-slate-800 px-8 py-3 rounded-lg font-medium transition-all"
          >
            Back to Sign Up
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-sm text-slate-500">
          <p>Having trouble? Contact support or ask your team administrator for help.</p>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-lg font-medium text-slate-800">Validating invitation...</div>
        </div>
      </div>
    )
  }

  if (error && !invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-xl border border-red-200">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2">Invalid Invitation</h3>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-xl border border-green-200">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h3 className="text-2xl font-semibold text-slate-800 mb-2">Welcome to {invitationData.tenant_name}!</h3>
            <p className="text-slate-600 mb-6">
              You've successfully joined the organization as a {invitationData.role}.
            </p>
            <p className="text-sm text-slate-500">
              Redirecting you to the app...
            </p>
          </div>
        </div>
      </div>
    )
  }

  // CRITICAL: This check must come before the user check
  if (showEmailConfirmation) {
    return <InvitationEmailConfirmationScreen />
  }

  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-xl border border-slate-200">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-800 mb-2">
              Join {invitationData.tenant_name}
            </h3>
            <p className="text-slate-600 mb-6">
              You've been invited to join <strong>{invitationData.tenant_name}</strong> as a {invitationData.role}.
            </p>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {error}
              </div>
            )}
            
            <button
              onClick={acceptInvitation}
              disabled={acceptLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all disabled:opacity-50"
            >
              {acceptLoading ? 'Joining...' : `Join ${invitationData.tenant_name}`}
            </button>
          </div>
        </div>
      </div>
    )
  }

 
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-2xl shadow-xl border border-slate-200">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Timer className="w-7 h-7 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-slate-900">You're Invited!</h2>
          <p className="mt-2 text-slate-600">
            Join <strong>{invitationData.tenant_name}</strong> on Logworthy
          </p>
        </div>

        {/* Invitation Details */}
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <div className="text-sm space-y-2">
            <div><strong>Organization:</strong> {invitationData.tenant_name}</div>
            <div><strong>Role:</strong> {invitationData.role.charAt(0).toUpperCase() + invitationData.role.slice(1)}</div>
            <div><strong>Email:</strong> {invitationData.invited_email}</div>
          </div>
        </div>

        {!showPasswordForm ? (
          <div className="space-y-4">
            <p className="text-center text-slate-600">
              To join this organization, you need to sign in or create an account.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={handleSignIn}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all flex items-center justify-center space-x-2"
              >
                <LogIn className="w-5 h-5" />
                <span>I have a Logworthy account</span>
              </button>
              
              <button
                onClick={handleCreateAccount}
                className="w-full border-2 border-blue-200 text-blue-600 hover:bg-blue-50 py-3 rounded-lg font-medium transition-all flex items-center justify-center space-x-2"
              >
                <User className="w-5 h-5" />
                <span>I'm new to Logworthy</span>
              </button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              Not sure? Try signing in first - if that doesn't work, create a new account.
            </p>
          </div>
        ) : (
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            {isNewUser && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg bg-slate-50 text-slate-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  minLength="6"
                  required
                />
              </div>
              {isNewUser && (
                <p className="mt-1 text-xs text-slate-500">
                  Must be at least 6 characters
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
                {error.includes('Try creating an account instead') && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsNewUser(true)
                        setError('')
                      }}
                      className="text-red-600 hover:text-red-700 font-medium underline"
                    >
                      Switch to Create Account
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false)
                  setError('')
                }}
                className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={acceptLoading}
                className="flex-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50"
              >
                {acceptLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{isNewUser ? 'Creating...' : 'Signing In...'}</span>
                  </div>
                ) : (
                  isNewUser ? 'Create Account & Join' : 'Sign In & Join'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default InvitationPage